import { useEffect, useMemo, useRef, useState } from 'react';
import { Check, ChevronDown, X } from 'lucide-react';
import type { PickerUser } from '@/services/users.service';
import { SearchInput } from '@/components/ui/search-input';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';

interface Props {
  /** Candidates only — callers leave out people already in the list. */
  people: PickerUser[];
  value: string[];
  onChange: (ids: string[]) => void;
  isLoading?: boolean;
  disabled?: boolean;
}

const UNASSIGNED_DEPT = 'No Department';

function fullName(u: PickerUser): string {
  return `${u.first_name} ${u.last_name}`.trim();
}

function initials(u: PickerUser): string {
  return `${u.first_name.charAt(0)}${u.last_name.charAt(0)}`.toUpperCase();
}

/**
 * Searchable multi-select employee picker for the DM Team access lists.
 *
 * Local to dm-team on purpose: tasks' AssigneePicker and goals' GoalAssigneeSelect
 * are similar, but importing across modules is banned. Like GoalAssigneeSelect it
 * renders an inline panel rather than a Radix Popover, so it keeps working if a
 * section is ever placed inside a Dialog (portalled popovers lose focus there).
 */
export function DmUserMultiPicker({ people, value, onChange, isLoading, disabled }: Props) {
  const [open, setOpenState] = useState(false);
  const [search, setSearch] = useState('');
  // Every close path goes through here, so the next open starts unfiltered.
  function setOpen(next: boolean) {
    setOpenState(next);
    if (!next) setSearch('');
  }
  const rootRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (!open) return;
    function onPointerDown(e: PointerEvent) {
      if (!rootRef.current?.contains(e.target as Node)) setOpen(false);
    }
    function onKeyDown(e: KeyboardEvent) {
      if (e.key === 'Escape') {
        e.stopPropagation();
        setOpen(false);
      }
    }
    document.addEventListener('pointerdown', onPointerDown);
    document.addEventListener('keydown', onKeyDown, true);
    return () => {
      document.removeEventListener('pointerdown', onPointerDown);
      document.removeEventListener('keydown', onKeyDown, true);
    };
  }, [open]);

  useEffect(() => {
    if (open) inputRef.current?.focus();
  }, [open]);

  const grouped = useMemo(() => {
    const q = search.trim().toLowerCase();
    const filtered = q
      ? people.filter((u) =>
          fullName(u).toLowerCase().includes(q) ||
          (u.employee_code ?? '').toLowerCase().includes(q) ||
          (u.department_name ?? '').toLowerCase().includes(q))
      : people;
    const map = new Map<string, PickerUser[]>();
    for (const u of filtered) {
      const dept = u.department_name ?? UNASSIGNED_DEPT;
      if (!map.has(dept)) map.set(dept, []);
      map.get(dept)!.push(u);
    }
    for (const list of map.values()) list.sort((a, b) => fullName(a).localeCompare(fullName(b)));
    return [...map.entries()].sort((a, b) => a[0].localeCompare(b[0]));
  }, [people, search]);

  function toggle(id: string) {
    onChange(value.includes(id) ? value.filter((x) => x !== id) : [...value, id]);
  }

  const triggerText = (() => {
    if (value.length === 0) return 'Select employees…';
    if (value.length === 1) {
      const only = people.find((p) => p.id === value[0]);
      return only ? fullName(only) : '1 employee selected';
    }
    return `${value.length} employees selected`;
  })();

  return (
    <div ref={rootRef} className="relative min-w-0 flex-1">
      <Button
        type="button"
        variant="outline"
        disabled={disabled}
        aria-expanded={open}
        onClick={() => setOpen(!open)}
        className="w-full justify-between font-normal"
      >
        <span className={cn('truncate', value.length === 0 && 'text-muted-foreground')}>
          {triggerText}
        </span>
        <ChevronDown className={cn('h-4 w-4 shrink-0 opacity-50 transition-transform', open && 'rotate-180')} />
      </Button>

      {open && (
        <div className="absolute left-0 top-full z-50 mt-1 w-full min-w-[280px] overflow-hidden rounded-xl border border-border/60 bg-card shadow-lg">
          <div className="border-b border-border/60 p-2">
            <SearchInput
              ref={inputRef}
              value={search}
              onChange={setSearch}
              placeholder="Search by name, code or department…"
              containerClassName="w-full"
            />
          </div>

          <div className="thin-scrollbar max-h-72 overflow-y-auto overscroll-contain p-1">
            {isLoading && (
              <div className="space-y-1.5 p-2">
                {[...Array(5)].map((_, i) => (
                  <div key={i} className="h-10 animate-pulse rounded-md bg-muted/60" />
                ))}
              </div>
            )}
            {!isLoading && people.length === 0 && (
              <p className="p-4 text-center text-sm text-muted-foreground">Everyone is already in this list</p>
            )}
            {!isLoading && people.length > 0 && grouped.length === 0 && (
              <p className="p-4 text-center text-sm text-muted-foreground">No employees found</p>
            )}
            {grouped.map(([dept, users]) => (
              <div key={dept} className="mb-0.5">
                {/* z-[1] only has to beat its sibling rows while scrolling. */}
                <div className="sticky top-0 z-[1] bg-card/95 px-2 py-1 text-[10px] font-semibold uppercase tracking-wide text-muted-foreground backdrop-blur-sm">
                  {dept}
                </div>
                {users.map((u) => {
                  const isSel = value.includes(u.id);
                  return (
                    <button
                      type="button"
                      key={u.id}
                      onClick={() => toggle(u.id)}
                      className={cn(
                        'flex min-h-[44px] w-full items-center gap-2.5 rounded-md px-2 py-2 text-left transition-colors',
                        isSel ? 'bg-primary/10 hover:bg-primary/15' : 'hover:bg-muted/50',
                      )}
                    >
                      <span className={cn(
                        'flex h-4 w-4 shrink-0 items-center justify-center rounded border',
                        isSel ? 'border-primary bg-primary text-primary-foreground' : 'border-border',
                      )}>
                        {isSel && <Check className="h-3 w-3" />}
                      </span>
                      <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-muted text-[10px] font-semibold text-foreground">
                        {initials(u)}
                      </span>
                      <span className="min-w-0 flex-1 truncate text-sm font-medium text-foreground">
                        {fullName(u)}
                      </span>
                      {u.employee_code && (
                        <span className="shrink-0 text-xs text-muted-foreground">{u.employee_code}</span>
                      )}
                    </button>
                  );
                })}
              </div>
            ))}
          </div>

          {value.length > 0 && (
            <div className="flex items-center justify-between gap-2 border-t border-border/60 px-3 py-2">
              <span className="text-xs text-muted-foreground">{value.length} selected</span>
              <button
                type="button"
                className="flex items-center gap-1 text-xs text-primary hover:underline"
                onClick={() => onChange([])}
              >
                <X className="h-3 w-3" /> Clear
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
